import { defineStore } from 'pinia';

/**
 * Central application store (migrated from Vuex).
 *
 * The original Vuex store was flat, action-less and used only same-name
 * passthrough getters, so this maps 1:1 onto a single Pinia store:
 *   - Vuex state      -> state
 *   - Vuex mutations  -> actions (set / mutate / remove methods)
 *   - Vuex getters    -> dropped (they were either unused get-prefixed aliases or
 *                        passthroughs of state with the same name; consumers
 *                        read the state directly via `mapState`).
 */
export const useSurveyStore = defineStore('survey', {
  state: () => ({
    language: 'en',

    background: null,
    surveyIdentifier: '',
    submissionToken: '',
    cliniqueId: null,
    cliniqueVerificationToken: '',
    questionGroups: [],
    diagnosisCategories: [],
    treatmentCategories: [],
    questionAnswers: {},
    surveyResult: null,

    disableTransitions: false,
    transitionDurationScale: 1,

    consent: {
      contactEmail: null,
      feedbackSurvey: null,
    },

    isAnonymous: null,

    // Previously written by SET_FEEDBACK_TYPE but missing from initial state;
    // declared here so it is reactive and discoverable.
    feedbackType: null,

    questionVars: {
      // How many X have you told?
      toldFamily: false,
      toldFriends: false,
      toldPartner: false,
      toldOthers: false,
    },

    showQuestionAnswerIds: false,

    fromWindowVar: {
      surveyIdentifier: false,
      submissionToken: false,
      cliniqueId: false,
      cliniqueVerificationToken: false,
    },
  }),

  actions: {
    setLanguage(language) {
      this.language = language;
    },
    setBackground(background) {
      this.background = background;
    },
    setSurveyIdentifier(surveyIdentifier, fromWindowVar = false) {
      this.surveyIdentifier = surveyIdentifier;

      this.fromWindowVar.surveyIdentifier = fromWindowVar;
    },
    setSubmissionToken(submissionToken, fromWindowVar = false) {
      this.submissionToken = submissionToken;

      this.fromWindowVar.submissionToken = fromWindowVar;
    },
    setCliniqueId(cliniqueId, fromWindowVar = false) {
      this.cliniqueId = cliniqueId;

      this.fromWindowVar.cliniqueId = fromWindowVar;
    },
    setCliniqueVerificationToken(cliniqueVerificationToken, fromWindowVar = false) {
      this.cliniqueVerificationToken = cliniqueVerificationToken;

      this.fromWindowVar.cliniqueVerificationToken = fromWindowVar;
    },
    setQuestionGroups(questionGroups) {
      this.questionGroups = questionGroups;
    },
    setQuestionAnswers(questionAnswers) {
      this.questionAnswers = questionAnswers;
    },
    setQuestionAnswer({ questionId, answer }) {
      this.questionAnswers[questionId] = answer;
    },
    removeQuestionAnswer(questionId) {
      delete this.questionAnswers[questionId];
    },
    setTreatmentCategories(treatmentCategories) {
      this.treatmentCategories = treatmentCategories;
    },
    setDiagnosisCategories(diagnosisCategories) {
      this.diagnosisCategories = diagnosisCategories;
    },
    setSurveyResult(surveyResult) {
      this.surveyResult = surveyResult;
    },
    setDisableTransitions(disableTransitions) {
      this.disableTransitions = !!disableTransitions;
    },
    setTransitionDurationScale(transitionDurationScale) {
      this.transitionDurationScale = transitionDurationScale;
    },
    setConsent(consent) {
      this.consent = consent;
    },
    setIsAnonymous(isAnonymous) {
      this.isAnonymous = isAnonymous;
    },
    setFeedbackType(feedbackType) {
      this.feedbackType = feedbackType;
    },
    setShowQuestionAnswerIds(showQuestionAnswerIds) {
      this.showQuestionAnswerIds = !!showQuestionAnswerIds;
    },
    mutateQuestionVariable({ variableName, callback }) {
      const questionVarKeys = Object.keys(this.questionVars);

      const currentValue = questionVarKeys.includes(variableName)
        ? this.questionVars[variableName]
        : null;

      this.questionVars[variableName] = callback(currentValue);
    },
  },
});
